import { useState, useEffect, useCallback } from 'react';
import { activityLogAPI } from '../../../services/activityTracker';

export interface ActivityLogEntry {
  id: number;
  user: number | null;
  username_snapshot: string;
  user_display: string;
  company_name: string;
  action_type: string;
  action_display: string;
  ip_address: string;
  user_agent: string;
  path: string;
  method: string;
  details: Record<string, any>;
  geo_country: string;
  geo_city: string;
  geo_region: string;
  geo_lat: number | null;
  geo_lon: number | null;
  geo_isp: string;
  geo_resolved: boolean;
  session_id: string;
  timestamp: string;
}

export interface UserSummary {
  user_id: number;
  username: string;
  company_name: string;
  customer_id: string;
  total_actions: number;
  last_login: string | null;
  last_login_ip: string | null;
  last_login_city: string | null;
  last_login_country: string | null;
  last_activity: string | null;
  login_count: number;
  unique_ips: number;
  page_visits: number;
  orders_placed: number;
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ActivityLogEntry[];
}

export function useAdminActivityLogs() {
  const [activeTab, setActiveTab] = useState('logs');
  const [data, setData] = useState<{
    logs: ActivityLogEntry[];
    summaries: UserSummary[];
    actionTypes: [string, string][];
  }>({ logs: [], summaries: [], actionTypes: [] });
  const { logs, summaries, actionTypes } = data;
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ totalCount: 0, currentPage: 1 });
  const { totalCount, currentPage } = pagination;
  
  // Filters
  const [filters, setFilters] = useState({
    filterAction: '',
    filterDateFrom: '',
    filterDateTo: '',
    filterSearch: '',
    selectedUserId: '',
  });
  const { filterAction, filterDateFrom, filterDateTo, filterSearch, selectedUserId } = filters;
  
  // Expanded row
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: page.toString() };
      if (filterAction) params.action_type = filterAction;
      if (selectedUserId) params.user_id = selectedUserId;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterSearch) params.search = filterSearch;
      
      const response = await activityLogAPI.getLogs(params);
      const responseData = response.data as PaginatedResponse;
      setData(prev => ({ ...prev, logs: responseData.results }));
      setPagination({ totalCount: responseData.count, currentPage: page });
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterDateFrom, filterDateTo, filterSearch, selectedUserId]);

  const fetchSummaries = useCallback(async () => {
    try {
      const response = await activityLogAPI.getSummary();
      setData(prev => ({ ...prev, summaries: response.data }));
    } catch (err) {
      console.error('Failed to fetch summaries:', err);
    }
  }, []);

  const fetchActionTypes = useCallback(async () => {
    try {
      const response = await activityLogAPI.getActionTypes();
      setData(prev => ({ ...prev, actionTypes: response.data }));
    } catch (err) {
      console.error('Failed to fetch action types:', err);
    }
  }, []);

  useEffect(() => {
    fetchActionTypes();
    fetchSummaries();
  }, [fetchActionTypes, fetchSummaries]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs(1);
    }
  }, [activeTab, fetchLogs]);

  const handleResolveGeo = async () => {
    try {
      await activityLogAPI.resolveGeo();
      setTimeout(() => fetchLogs(currentPage), 3000);
    } catch (err) {
      console.error('Failed to resolve geo:', err);
    }
  };

  const clearFilters = () => {
    setFilters({
      filterAction: '',
      filterDateFrom: '',
      filterDateTo: '',
      filterSearch: '',
      selectedUserId: '',
    });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    }) + ' ' + d.toLocaleTimeString('en-US', { 
      hour: '2-digit', minute: '2-digit', second: '2-digit' 
    });
  };

  const formatTimeAgo = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
  };

  const parseUserAgent = (ua: string) => {
    if (!ua) return 'Unknown';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Linux')) return 'Linux';
    return 'Other';
  };

  const parseBrowser = (ua: string) => {
    if (!ua) return 'Unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Edg')) return 'Edge';
    return 'Other';
  };

  const totalPages = Math.ceil(totalCount / 50);

  return {
    // Tab
    activeTab, setActiveTab,
    // Data
    logs, summaries, actionTypes,
    loading,
    totalCount, currentPage,
    // Filters
    filters, setFilters,
    filterAction, filterDateFrom, filterDateTo, filterSearch, selectedUserId,
    // Expanded row
    expandedLogId, setExpandedLogId,
    // Actions
    fetchLogs, fetchSummaries,
    handleResolveGeo, clearFilters,
    // Formatters
    formatDate, formatTimeAgo, parseUserAgent, parseBrowser,
    // Computed
    totalPages,
  };
}
